import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "store/store";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import styles from "./BrandProfileStyles.module.scss";
import { useTranslation } from "react-i18next";
import { Asset } from "@switcherstudio/switcher-api-client";
import { RootState } from "store/reducers";
import { AssetCardDelete } from "components/asset-card-delete/AssetCardDelete";
import rollbar from "helpers/rollbar";
import { useInterval } from "hooks/useInterval";
import { AttentionModal } from "components/modal/AttentionModal";
import { Modals } from "store/modal/types";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { useSwitcherClient } from "hooks/useSwitcherClient";

interface UploadedLogoProps {
    logos: Asset[];
    refreshLogos: () => Promise<Asset[]>;
    brandId: string;
}

export const UploadedLogo: React.FC<UploadedLogoProps> = ({
    logos,
    refreshLogos,
    brandId
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const userInfo = useSelector((state: RootState) => state.user.userInfo);
    const [userId, setUserId] = useState<string>(null);
    const [asset, setAsset] = useState<Asset>(null);
    const { t } = useTranslation();

    useEffect(() => {
        if (!!userInfo && userId === null)
            setUserId(userInfo?.User?.Details?.Id);
    }, [userInfo, userId]);

    // Poll for asset status updates
    const [delay, setDelay] = useState(3000);
    const [retryCount, setRetryCount] = useState(0);

    useInterval(async () => {
        // If all assets are uploaded and have thumbnails, stop polling
        const allAssetsUploaded = logos.every(
            (a) => (a.Status as any) === "Uploaded" && a.ThumbnailUrl !== null
        );

        // Set delay to null to clear this interval
        if (allAssetsUploaded) setDelay(null);

        // Refresh logos
        await refreshLogos();

        // Configure dynamic retry for polling
        const BACKOFF_MIN = 3;
        const BACKOFF_MAX = 12;
        const BACKOFF_EXPONENT = 1.5;

        if (retryCount > BACKOFF_MIN) {
            // After a certain number of retries, start increasing the delay between retries
            setDelay(delay * BACKOFF_EXPONENT);
        }

        if (retryCount > BACKOFF_MAX) {
            // Eventually, give up and notify the user
            setDelay(null);
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: "errors:logo-upload-timeout-error"
                })
            );
        }

        setRetryCount(retryCount + 1);
    }, delay);

    const { dispatchApiRequest: deleteBrandLogo } = useSwitcherClient(
        (client) => client.brandLogos_DeleteUserBrandLogo,
        {
            requestImmediately: false,
            args: [userId, brandId, asset?.Id],
            onSuccess: async () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: "messages:delete-logo-success"
                    })
                );
                await refreshLogos();
            },
            onError: (e) => {
                rollbar.error("Error deleting user brand logo", e);
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "errors:delete-logo-error"
                    })
                );
            }
        }
    );

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const openAttentionModal = useCallback(
        () =>
            dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeAttentionModal}
                            handleCancel={closeAttentionModal}
                            hasContinueButton={true}
                            handleContinue={deleteBrandLogo}
                            continueText={t("buttons:remove")}
                        >
                            <div>{t("messages:delete-logo-confirmation")}</div>
                        </AttentionModal>
                    )
                })
            ),
        [closeAttentionModal, dispatch, t, deleteBrandLogo]
    );

    const deleteConfirmation = useCallback(
        async (asset) => {
            if (userId === null) {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "user-info-load-error"
                    })
                );

                rollbar.error("UploadedLogo: userId is null");
                return;
            }

            setAsset(asset);
            openAttentionModal();
        },
        [dispatch, userId, openAttentionModal]
    );

    return (
        <>
            <div className={styles["logo-uploader"]}>
                <h4 className={styles["form-card-title"]}>
                    {t("brand-profile:brand-logo")}
                </h4>
                <div className={styles["logo-previews"]}>
                    {logos.map((l) => (
                        <div className={styles["logo-thumbnail"]} key={l?.Id}>
                            <AssetCardDelete
                                asset={l}
                                onClick={() => deleteConfirmation(l)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};
